import React, { useEffect, useState } from 'react';
import { Flex, Icon, Image, Text } from '@chakra-ui/react';
import { FiChevronDown } from 'react-icons/fi';
import LocationSearchModal from '../../explore/components/LocationSearchModal';
import { PlaceDetails } from '../../../types';
import { ff } from '../../../theme';
import nearMe from '../assets/near-me.png';
import { boardLocationLabel, type BoardNearMeLocation } from '../boardLocationUtils';

type Props = {
  selected: boolean;
  location: BoardNearMeLocation | null;
  onToggle: () => void;
  onLocationChange: (location: BoardNearMeLocation | null, radius: number) => void;
  openPickerOnMount?: boolean;
};

/** Near me filter — one chip; when active it turns ink and its location suffix opens the city picker. */
const NearMeChip: React.FC<Props> = ({
  selected,
  location,
  onToggle,
  onLocationChange,
  openPickerOnMount,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (openPickerOnMount) setIsOpen(true);
  }, [openPickerOnMount]);

  const handleSelect = (place: PlaceDetails, radius: number) => {
    onLocationChange({ ...place, radius }, radius);
    setIsOpen(false);
  };

  const handleClear = () => {
    onLocationChange(null, location?.radius ?? 25);
    setIsOpen(false);
  };

  const locationSuffix = location ? boardLocationLabel(location) : 'Set location';

  const modal = (
    <LocationSearchModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      onLocationSelect={handleSelect}
      currentLocation={location}
      onClearLocation={handleClear}
      radius={location?.radius ?? 25}
      title="Where should we look?"
      currentLocationHint="Finds bounties right where you are"
    />
  );

  if (!selected) {
    return (
      <>
        <Flex
          as="button"
          type="button"
          aria-pressed={false}
          flex="none"
          h="44px"
          px="14px"
          pl="12px"
          gap="6px"
          align="center"
          borderRadius="full"
          border="1px solid"
          borderColor={ff.border}
          bg="white"
          color="#3E3E42"
          fontSize="14px"
          fontWeight={600}
          _hover={{ bg: ff.grey }}
          onClick={onToggle}
        >
          <Image src={nearMe} alt="" w="18px" h="18px" objectFit="contain" flex="none" />
          Near me
        </Flex>
        {modal}
      </>
    );
  }

  return (
    <>
      <Flex flex="none" h="44px" align="center" borderRadius="full" bg={ff.ink} color="white" fontSize="14px" fontWeight={600} overflow="hidden">
        <Flex
          as="button"
          type="button"
          aria-pressed
          h="full"
          pl="12px"
          pr="8px"
          gap="6px"
          align="center"
          onClick={onToggle}
        >
          <Image src={nearMe} alt="" w="18px" h="18px" objectFit="contain" flex="none" />
          Near me
        </Flex>
        <Text color={ff.muted} userSelect="none" aria-hidden>·</Text>
        <Flex
          as="button"
          type="button"
          aria-label="Change location"
          h="full"
          pl="8px"
          pr="14px"
          align="center"
          onClick={() => setIsOpen(true)}
        >
          <Text noOfLines={1} maxW="140px">{locationSuffix}</Text>
          <Icon as={FiChevronDown} boxSize="15px" color={ff.lime} ml="4px" flexShrink={0} />
        </Flex>
      </Flex>
      {modal}
    </>
  );
};

export default NearMeChip;
